/**
 * Should the open session be marked read right now?
 *
 * The rule is "whatever you are looking at is read", re-evaluated on every idle
 * poll so that messages arriving WHILE you watch clear too, not just the backlog
 * you opened with. What makes it more than a boolean is that /agent/mark_read is a
 * POST and the poll runs every few seconds, so the naive version re-posts on every
 * tick until the count comes back zero.
 *
 * The previous guard remembered the session and never released it, so the first
 * batch cleared and nothing after it ever did — a badge that reappeared while you
 * were reading and then stayed. It shows up most on remote agents because those are
 * the ones that answer while you are still watching.
 *
 * So the memory is of the session AND the count. A count we have already acted on is
 * not acted on twice — which also means a server that does not clear it cannot spin
 * us in a loop — while any NEW message changes the count and re-arms.
 *
 * `null` re-arms whenever the badge is genuinely clear, so a later run of unread
 * that happens to land on the same number is not mistaken for the one we handled.
 */
export function nextMarkRead(
  seen: string | null,
  key: string | null,
  unread: number,
): { post: boolean; seen: string | null } {
  if (!key) return { post: false, seen: null }
  if (unread <= 0) return { post: false, seen: null }
  const signature = `${key}\u0000${unread}`
  if (seen === signature) return { post: false, seen }
  return { post: true, seen: signature }
}

/**
 * Is the open conversation actually IN FRONT OF THE USER right now?
 *
 * nextMarkRead answers "has this count been handled"; this answers the question it
 * assumes — that having something selected means you are reading it. That assumption
 * is what made the owner's own agent the one row whose badge never appeared: the
 * count was accumulating correctly (verified on the wire: two other agents held
 * unread 2 and 3 at the same moment this one held 0), and it was being cleared
 * within one round trip of arriving, because a selection is kept while the rail, a
 * panel or another app is covering the thing selected.
 *
 * It never appeared rather than flickering because clearing also invalidates the
 * poll, so the badge's whole life was one request — visible in the data, invisible
 * on screen. The owner found it by accident, from the one angle that dodges the
 * rule: sending from Project Chat, where the agent is not the selection.
 *
 * The three ways a selected conversation can be off-screen, all of them states the
 * app already tracks:
 *   - the tab is in the background, or the phone is locked
 *   - the rail is covering it, which on a phone is a full screen and the ROOT one
 *   - a sheet is over the stage — and the Activity panel is the sharpest case,
 *     since that is where someone goes precisely to see what arrived
 *
 * Deliberately NOT included: which view mode is showing. Terminal and Clean View
 * both put the pane in front of you, and an inbound message is injected into the
 * pty, so in both the message is genuinely read.
 */
export function conversationOnScreen(s: {
  documentVisible: boolean
  railCovers: boolean
  overlayOpen: boolean
}): boolean {
  return s.documentVisible && !s.railCovers && !s.overlayOpen
}

/**
 * What to do when advancing a read cursor was refused.
 *
 * Extracted for the same reason as nextMarkRead: the interesting part is a decision,
 * and a decision inside an effect can only be checked by reasoning about it.
 *
 * `permanent` means the guard stays set — the server declined and will decline the
 * same way on the next poll, so re-asking is twelve requests a minute that cannot
 * succeed. That was a real production loop on remote agents answering
 * `host_not_mounted`.
 *
 * `refreshCsrf` is the exception, and only /project/* has it: those POSTs require
 * X-CSRF-Token and the token expires. Filed as permanent, a stale token would freeze
 * the cursor and leave the badge lit forever with nothing to report — the failure
 * this feature exists to prevent, arriving from the other side. Re-arm, and refetch
 * the endpoint that issues the token.
 */
export function markReadRefusal(
  status: number | null,
  message: string,
): { permanent: boolean; refreshCsrf: boolean } {
  if (status === 403 && /csrf/i.test(message)) return { permanent: false, refreshCsrf: true }
  if (status !== null && status >= 400 && status < 500) return { permanent: true, refreshCsrf: false }
  // A network error or a 5xx is the request not arriving, not the server refusing.
  return { permanent: false, refreshCsrf: false }
}
